import React, { useState, useCallback, useEffect, useRef } from 'react';
import { getPortalUrl } from '../../lib/portal';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  X as XIcon,
  ChevronLeft,
  Upload,
  Check,
  Info,
  AlertTriangle,
  Wifi,
  Download,
  FileImage,
} from 'lucide-react';
import QRCode from 'qrcode';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Input, Textarea } from '../UI/Input';
import { QuotaLimitModal } from '../UI/QuotaLimitModal';
import { useQuotaGuard } from '../../hooks/useQuotaGuard';
import { eventsApi } from '../../api/events';
import { albumsApi } from '../../api/albums';
import { overlaysApi } from '../../api/overlays';
import { InviteLinkPanel } from '../Albums/InviteLinkPanel';
import { A4PosterModal } from '../Albums/A4PosterModal';
import { OverlayConfigCard } from '../Albums/OverlayConfigCard';
import { LiveBadge } from '../UI/LiveBadge';
import { FaceRecognitionStep, FaceRecognitionConfig } from '../Albums/FaceRecognitionStep';
import { Event, Album, Overlay, AlbumOverlayConfig, OverlayAnchor } from '../../types';
import { brandingApi } from '../../api/settings';
import { useAuthStore } from '../../store/authStore';
import { getReservedAnchors, pickFreeAnchor } from '../../lib/overlayAnchors';
import { track, EVENTS } from '../../lib/mixpanel';
import styles from './CreateEventModal.module.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (event: Event) => void;
}

type WizardStep =
  | 'create-event'
  | 'live-prompt'
  | 'album-info'
  | 'overlay-upload'
  | 'overlay-config'
  | 'face-recognition'
  | 'success';

// ─── Animation variants ──────────────────────────────────────────────────────

const stepVariants = {
  enter: { opacity: 0, x: 24 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};

const stepTransition = { type: 'spring' as const, stiffness: 340, damping: 32 };

// ─── QR Code display ──────────────────────────────────────────────────────────

function QRCodeDisplay({
  inviteToken,
  onOpenPoster,
}: {
  inviteToken: string;
  onOpenPoster?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const inviteUrl = `${getPortalUrl('viewer')}/join/${inviteToken}`;
    QRCode.toCanvas(canvasRef.current, inviteUrl, {
      width: 220,
      margin: 2,
      color: { dark: '#1a0000', light: '#ffffff' },
      errorCorrectionLevel: 'M',
    });
  }, [inviteToken]);

  function handleDownload() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'invite-qr-code.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  return (
    <div className={styles['qr-display']}>
      <div className={styles['qr-canvas-wrap']}>
        <canvas ref={canvasRef} />
      </div>
      <div className={styles['qr-actions']}>
        <Button variant="secondary" size="sm" onClick={handleDownload}>
          <Download style={{ width: 14, height: 14 }} />
          Download QR Code
        </Button>
        {onOpenPoster && (
          <Button variant="secondary" size="sm" onClick={onOpenPoster}>
            <FileImage style={{ width: 14, height: 14 }} />
            Download Poster
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Mini step dots for the live album sub-wizard ────────────────────────────

const LIVE_STEPS: { key: WizardStep; label: string }[] = [
  { key: 'album-info', label: 'Details' },
  { key: 'overlay-upload', label: 'Overlays' },
  { key: 'overlay-config', label: 'Config' },
  { key: 'face-recognition', label: 'Face' },
];

function LiveWizardDots({ currentStep }: { currentStep: WizardStep }) {
  const currentIdx = LIVE_STEPS.findIndex((s) => s.key === currentStep);

  return (
    <div className={styles['step-dots']}>
      {LIVE_STEPS.map((s, i) => {
        const done = currentIdx > i;
        const active = currentIdx === i;
        return (
          <React.Fragment key={s.key}>
            <div className={styles['step-dot-wrapper']}>
              <div
                className={[
                  styles['step-dot'],
                  done ? styles['step-dot-done'] : active ? styles['step-dot-active'] : styles['step-dot-pending'],
                ].join(' ')}
              >
                {done ? <Check className={styles['icon-sm']} strokeWidth={3} /> : i + 1}
              </div>
              <span
                className={[
                  styles['step-dot-label'],
                  active || done ? styles['step-dot-label-active'] : styles['step-dot-label-inactive'],
                ].join(' ')}
              >
                {s.label}
              </span>
            </div>

            {i < LIVE_STEPS.length - 1 && (
              <div
                className={[
                  styles['step-connector'],
                  currentIdx > i ? styles['step-connector-done'] : styles['step-connector-pending'],
                ].join(' ')}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Overlay drag-and-drop upload area ───────────────────────────────────────

function OverlayDropZone({
  uploading,
  onFilesSelected,
}: {
  uploading: boolean;
  onFilesSelected: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files).filter(
        (f) => f.type === 'image/png'
      );
      if (files.length === 0) {
        toast.error('Only PNG files are accepted for overlays');
        return;
      }
      onFilesSelected(files);
    },
    [onFilesSelected]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []).filter(
        (f) => f.type === 'image/png'
      );
      if (files.length === 0) {
        toast.error('Only PNG files are accepted for overlays');
        return;
      }
      onFilesSelected(files);
      e.target.value = '';
    },
    [onFilesSelected]
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={[
        styles['drop-zone'],
        dragOver ? styles['drop-zone-active'] : '',
        uploading ? styles['drop-zone-disabled'] : '',
      ].filter(Boolean).join(' ')}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        multiple
        className={styles['hidden-input']}
        onChange={handleInputChange}
      />
      <Upload
        className={styles['drop-zone-upload-icon']}
      />
      <div className={styles['text-center']}>
        <p className={styles['drop-zone-text']}>
          {uploading ? 'Uploading...' : 'Drop PNG overlays here'}
        </p>
        <p className={styles['drop-zone-hint']}>
          or click to browse. PNG with transparency recommended.
        </p>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export const CreateEventModal: React.FC<CreateEventModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState('');
  const [createdEvent, setCreatedEvent] = useState<Event | null>(null);

  const [step, setStep] = useState<WizardStep>('create-event');

  const [albumName, setAlbumName] = useState('');
  const [albumDescription, setAlbumDescription] = useState('');
  const [uploadedOverlays, setUploadedOverlays] = useState<Overlay[]>([]);
  const [overlayConfigs, setOverlayConfigs] = useState<AlbumOverlayConfig[]>([]);
  const [uploading, setUploading] = useState(false);
  const [albumCreating, setAlbumCreating] = useState(false);
  const [createdAlbum, setCreatedAlbum] = useState<Album | null>(null);
  const [faceConfig, setFaceConfig] = useState<FaceRecognitionConfig>({
    faceRecognitionEnabled: false,
    guestVisibility: 'all',
    faceSearchScope: 'event',
  });

  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [defaultAnchor, setDefaultAnchor] = useState<OverlayAnchor | null>(null);
  const [attachDefaultOverlay, setAttachDefaultOverlay] = useState(true);
  const [showPoster, setShowPoster] = useState(false);

  const { quotaModal, handleQuotaError, closeQuotaModal } = useQuotaGuard();

  const isOwner = useAuthStore((s) => s.user?.isOwner ?? false);

  // Non-owners always get the default Pixel Machha watermark (enforced
  // server-side); owners may opt out for this album only.
  const defaultBranded = !isOwner || attachDefaultOverlay;

  // Fetch the system default branding overlay anchor for the reserved-anchor logic
  useEffect(() => {
    if (!isOpen || defaultAnchor) return;
    brandingApi.getDefaults()
      .then((d) => setDefaultAnchor(d.config.anchor))
      .catch(() => {/* non-critical */});
  }, [isOpen, defaultAnchor]);

  const resetAll = useCallback(() => {
    setEventName('');
    setEventDescription('');
    setEventLoading(false);
    setEventError('');
    setCreatedEvent(null);
    setStep('create-event');
    setAlbumName('');
    setAlbumDescription('');
    setUploadedOverlays([]);
    setOverlayConfigs([]);
    setUploading(false);
    setAlbumCreating(false);
    setCreatedAlbum(null);
    setShowSkipWarning(false);
    setAttachDefaultOverlay(true);
    setShowPoster(false);
    setFaceConfig({
      faceRecognitionEnabled: false,
      guestVisibility: 'all',
      faceSearchScope: 'event',
    });
  }, []);

  const handleClose = useCallback(() => {
    resetAll();
    onClose();
  }, [resetAll, onClose]);

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventName.trim()) return;

    setEventLoading(true);
    setEventError('');
    try {
      const event = await eventsApi.create({
        name: eventName.trim(),
        description: eventDescription.trim(),
      });
      track(EVENTS.EVENT_CREATED, {
        event_id: event._id,
        event_name: event.name,
        has_description: !!eventDescription.trim(),
      });
      onCreated(event);
      setCreatedEvent(event);
      setAlbumName(`${event.name} Live`);
      setStep('live-prompt');
    } catch (err: unknown) {
      if (handleQuotaError(err)) return;
      const error = err as { response?: { data?: { error?: string } } };
      const msg = error.response?.data?.error || 'Failed to create event';
      setEventError(msg);
      toast.error(msg);
    } finally {
      setEventLoading(false);
    }
  };

  const handleUploadOverlays = async (files: File[]) => {
    if (!createdEvent) return;
    setUploading(true);
    try {
      const results: Overlay[] = [];
      for (const file of files) {
        const name = file.name.replace(/\.png$/i, '');
        const overlay = await overlaysApi.upload(createdEvent._id, name, file);
        results.push(overlay);
      }
      setUploadedOverlays((prev) => [...prev, ...results]);
      setOverlayConfigs((prev) => {
        // Pick a free anchor for each newly uploaded overlay so they don't
        // all stack on the same position.
        const next = [...prev];
        for (const o of results) {
          const reserved = getReservedAnchors(next, -1, {
            defaultBranded,
            defaultAnchor: defaultAnchor ?? undefined,
          });
          const anchor = pickFreeAnchor(reserved);
          next.push({
            overlayId: o._id,
            anchor,
            size: 'md' as const,
            opacity: 1,
          });
        }
        return next;
      });
      toast.success(
        `${results.length} overlay${results.length > 1 ? 's' : ''} uploaded`
      );
    } catch {
      toast.error('Failed to upload overlay');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveUploadedOverlay = (index: number) => {
    const overlay = uploadedOverlays[index];
    setUploadedOverlays((prev) => prev.filter((_, i) => i !== index));
    setOverlayConfigs((prev) =>
      prev.filter((c) => c.overlayId !== overlay._id)
    );
  };

  const handleCreateLiveAlbum = async () => {
    if (!createdEvent) return;
    setAlbumCreating(true);
    try {
      const album = await albumsApi.create({
        name: albumName.trim(),
        description: albumDescription.trim() || undefined,
        eventId: createdEvent._id,
        isLive: true,
        sourceFilter: 'all',
        // Owner-only opt-out; the server ignores it for everyone else.
        ...(isOwner && { attachDefaultOverlay }),
        faceRecognitionEnabled: faceConfig.faceRecognitionEnabled,
        ...(faceConfig.faceRecognitionEnabled && {
          guestVisibility: faceConfig.guestVisibility,
          faceSearchScope: faceConfig.faceSearchScope,
        }),
      });

      if (overlayConfigs.length > 0) {
        const updated = await albumsApi.update(album._id, { overlayConfigs });
        setCreatedAlbum(updated);
      } else {
        setCreatedAlbum(album);
      }

      track(EVENTS.ALBUM_CREATED, {
        album_id: album._id,
        event_id: createdEvent._id,
        is_live: true,
        has_overlays: overlayConfigs.length > 0,
        overlay_count: overlayConfigs.length,
        entry_point: 'event_creation',
      });

      setStep('success');
    } catch (err) {
      if (!handleQuotaError(err)) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || 'Failed to create live album');
      }
    } finally {
      setAlbumCreating(false);
    }
  };

  const handleUpdateOverlayConfig = (index: number, updated: AlbumOverlayConfig) => {
    setOverlayConfigs((prev) => prev.map((c, i) => (i === index ? updated : c)));
  };

  const handleRemoveOverlayConfig = (index: number) => {
    setOverlayConfigs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOverlayUploadNext = () => {
    if (uploadedOverlays.length === 0) {
      setShowSkipWarning(true);
    } else {
      setStep('overlay-config');
    }
  };

  const handleSkipOverlaysConfirmed = () => {
    setShowSkipWarning(false);
    setStep('face-recognition');
  };

  const titleMap: Record<WizardStep, string> = {
    'create-event': 'Create New Event',
    'live-prompt': 'Event Created!',
    'album-info': 'Live Album Details',
    'overlay-upload': 'Upload Overlays',
    'overlay-config': 'Configure Overlays',
    'face-recognition': 'Face Recognition',
    'success': 'Live Album Ready',
  };

  const isInLiveWizard = ['album-info', 'overlay-upload', 'overlay-config', 'face-recognition'].includes(step);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={titleMap[step]}
        size={step === 'success' ? 'md' : 'md'}
      >
        <div className={styles['modal-body']}>
          {isInLiveWizard && <LiveWizardDots currentStep={step} />}

          <AnimatePresence mode="wait">
            {/* ── Step: Create Event ── */}
            {step === 'create-event' && (
              <motion.div
                key="create-event"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={stepTransition}
              >
                <form onSubmit={handleCreateEvent} className={styles['form']}>
                  <Input
                    label="Event Name"
                    placeholder="e.g. Wedding Reception 2024"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    required
                    autoFocus
                  />
                  <Textarea
                    label="Description (optional)"
                    placeholder="Brief description of the event..."
                    value={eventDescription}
                    onChange={(e) => setEventDescription(e.target.value)}
                    rows={3}
                  />
                  {eventError && (
                    <p className={styles['error-banner']}>
                      {eventError}
                    </p>
                  )}
                  <div className={styles['form-actions']}>
                    <Button variant="ghost" type="button" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button type="submit" loading={eventLoading} disabled={!eventName.trim()}>
                      Create Event
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* ── Step: Live Album Prompt ── */}
            {step === 'live-prompt' && (
              <motion.div
                key="live-prompt"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={stepTransition}
                className={styles['step-content']}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 26 }}
                  className={styles['success-indicator']}
                >
                  <div className={styles['success-circle']}>
                    <Check className={styles['success-check-icon']} strokeWidth={2} />
                  </div>
                  <p className={styles['success-text']}>
                    <strong className={styles['success-strong']}>{createdEvent?.name}</strong>{' '}
                    has been created.
                  </p>
                </motion.div>

                <div className={styles['prompt-section']}>
                  <p className={styles['prompt-title']}>
                    Would you like to create a Live Album?
                  </p>
                  <p className={styles['prompt-text']}>
                    A live album lets guests see photos in real-time as they are uploaded. You can also add one later from the Albums section.
                  </p>
                </div>

                <div className={styles['option-stack']}>
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.985 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                    onClick={() => setStep('album-info')}
                    className={[styles['option-card'], styles['option-card-primary']].join(' ')}
                  >
                    <div className={styles['option-inner']}>
                      <div className={[styles['option-icon'], styles['option-icon-indigo']].join(' ')}>
                        <Zap className={styles['zap-icon-indigo']} />
                      </div>
                      <div className={styles['option-content']}>
                        <p className={styles['option-title']}>
                          Yes, create a Live Album
                        </p>
                        <p className={styles['option-desc']}>
                          Set up real-time photo sharing with overlays and QR invite
                        </p>
                      </div>
                    </div>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.985 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                    onClick={handleClose}
                    className={[styles['option-card'], styles['option-card-secondary']].join(' ')}
                  >
                    <div className={styles['option-inner']}>
                      <div className={[styles['option-icon'], styles['option-icon-neutral']].join(' ')}>
                        <XIcon className={styles['x-icon-secondary']} />
                      </div>
                      <div className={styles['option-content']}>
                        <p className={styles['option-title-secondary']}>
                          No, just the event
                        </p>
                        <p className={styles['option-desc']}>
                          You can always create albums later
                        </p>
                      </div>
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* ── Step A: Album Name + Description ── */}
            {step === 'album-info' && (
              <motion.div
                key="album-info"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={stepTransition}
                className={styles['form']}
              >
                <div className={styles['form']}>
                  <Input
                    label="Album Name"
                    placeholder="e.g. Wedding Day Live"
                    value={albumName}
                    onChange={(e) => setAlbumName(e.target.value)}
                    maxLength={80}
                    required
                    autoFocus
                  />
                  <Textarea
                    label="Description (optional)"
                    placeholder="A note for your guests..."
                    value={albumDescription}
                    onChange={(e) => setAlbumDescription(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className={styles['info-banner']}>
                  <Info className={styles['info-icon-indigo']} />
                  <p className={styles['info-text']}>
                    Photo sources default to <strong className={styles['info-strong']}>all sources</strong>.
                    You can configure specific FTP camera sources later from the{' '}
                    <strong className={styles['info-strong']}>Gallery &rarr; Albums</strong> section.
                  </p>
                </div>

                <div className={styles['nav-bar']}>
                  <Button variant="ghost" onClick={() => setStep('live-prompt')}>
                    <ChevronLeft className={styles['icon-md']} />
                    Back
                  </Button>
                  <Button onClick={() => setStep('overlay-upload')} disabled={!albumName.trim()}>
                    Next
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── Step B: Upload Overlays ── */}
            {step === 'overlay-upload' && (
              <motion.div
                key="overlay-upload"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={stepTransition}
                className={styles['form']}
              >
                <p className={styles['info-text']}>
                  Upload PNG overlays (logos, frames, watermarks) to be applied to photos in real-time. You can skip this step.
                </p>

                <OverlayDropZone
                  uploading={uploading}
                  onFilesSelected={handleUploadOverlays}
                />

                {uploadedOverlays.length > 0 && (
                  <div className={styles['form']}>
                    <p className={styles['uploaded-title']}>
                      Uploaded ({uploadedOverlays.length})
                    </p>
                    <div className={styles['uploaded-list']}>
                      {uploadedOverlays.map((overlay, i) => (
                        <div
                          key={overlay._id}
                          className={styles['uploaded-thumb']}
                        >
                          <img
                            src={overlaysApi.fileUrl(overlay._id)}
                            alt={overlay.name}
                            className={`${styles['uploaded-img']} ${styles['checkerboard-bg']}`}
                          />
                          <button
                            onClick={() => handleRemoveUploadedOverlay(i)}
                            className={styles['uploaded-remove']}
                          >
                            <XIcon className={styles['icon-xs']} />
                          </button>
                          <p className={styles['uploaded-name']}>
                            {overlay.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles['info-banner']}>
                  <Info className={`${styles['info-icon']} ${styles['info-icon-indigo']}`} />
                  <p className={styles['info-text']}>
                    You can configure photo sources later from the{' '}
                    <strong className={styles['info-strong']}>Gallery &rarr; Albums</strong> section.
                  </p>
                </div>

                {isOwner ? (
                  <label className={[styles['info-banner'], styles['branding-toggle']].join(' ')}>
                    <input
                      type="checkbox"
                      checked={attachDefaultOverlay}
                      onChange={(e) => setAttachDefaultOverlay(e.target.checked)}
                      className={styles['branding-checkbox']}
                      aria-label="Attach default Pixel Machha branding watermark"
                    />
                    <p className={styles['info-text']}>
                      Attach the <strong className={styles['info-strong']}>default Pixel Machha watermark</strong> to every photo in this album.
                      As an owner you can untick this to skip the default branding for this album only.
                    </p>
                  </label>
                ) : (
                  <div className={[styles['info-banner'], styles['info-banner-amber']].join(' ')}>
                    <span className={styles['warning-char']}>&#9888;</span>
                    <p className={styles['info-text']}>
                      A <strong className={styles['info-strong']}>Pixel Machha watermark</strong> is automatically applied to all album photos during beta.
                    </p>
                  </div>
                )}

                <div className={styles['nav-bar']}>
                  <Button variant="ghost" onClick={() => setStep('album-info')}>
                    <ChevronLeft className={styles['icon-md']} />
                    Back
                  </Button>
                  <Button onClick={handleOverlayUploadNext} disabled={uploading}>
                    {uploadedOverlays.length > 0 ? 'Next' : 'Skip'}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── Step C: Configure Overlays ── */}
            {step === 'overlay-config' && (
              <motion.div
                key="overlay-config"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={stepTransition}
                className={styles['form']}
              >
                <p className={styles['info-text']}>
                  Adjust the position, size, and opacity for each overlay. These are applied in real-time as photos arrive.
                </p>

                <div className={styles['config-scroll']}>
                  {overlayConfigs.length === 0 ? (
                    <div className={styles['config-empty']}>
                      No overlays configured
                    </div>
                  ) : (
                    overlayConfigs.map((config, i) => {
                      const overlay = uploadedOverlays.find(
                        (o) => o._id === config.overlayId
                      );
                      if (!overlay) return null;
                      const reservedAnchors = getReservedAnchors(overlayConfigs, i, {
                        defaultBranded,
                        defaultAnchor: defaultAnchor ?? undefined,
                      });
                      return (
                        <OverlayConfigCard
                          key={config.overlayId}
                          overlay={overlay}
                          config={config}
                          reservedAnchors={reservedAnchors}
                          onChange={(updated) => handleUpdateOverlayConfig(i, updated)}
                          onRemove={() => handleRemoveOverlayConfig(i)}
                        />
                      );
                    })
                  )}
                </div>

                <div className={styles['nav-bar']}>
                  <Button variant="ghost" onClick={() => setStep('overlay-upload')} disabled={albumCreating}>
                    <ChevronLeft className={styles['icon-md']} />
                    Back
                  </Button>
                  <Button onClick={() => setStep('face-recognition')} disabled={albumCreating}>
                    Next
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── Step: Face Recognition ── */}
            {step === 'face-recognition' && (
              <motion.div
                key="face-recognition"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={stepTransition}
              >
                <FaceRecognitionStep
                  config={faceConfig}
                  onChange={(patch) => setFaceConfig((prev) => ({ ...prev, ...patch }))}
                  isLive={true}
                  onNext={handleCreateLiveAlbum}
                  onBack={() => setStep('overlay-config')}
                />
              </motion.div>
            )}

            {/* ── Step D: Success ── */}
            {step === 'success' && createdAlbum && createdEvent && (
              <motion.div
                key="success"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={stepTransition}
                className={styles['step-content']}
              >
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                  className={styles['success-header']}
                >
                  <div className={styles['success-icon']}>
                    <Zap className={styles['zap-icon-lg']} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className={styles['success-name-row']}>
                      <p className={styles['success-album-name']}>
                        {createdAlbum.name}
                      </p>
                      <LiveBadge active />
                    </div>
                    <p className={styles['success-desc']}>
                      Your live album is ready. Share the invite link with your guests.
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                >
                  <QRCodeDisplay
                    inviteToken={createdAlbum.inviteToken}
                    onOpenPoster={() => setShowPoster(true)}
                  />
                </motion.div>

                <div className={styles['live-indicator']}>
                  <Wifi className={styles['wifi-icon-indigo']} />
                  <p className={styles['live-text']}>
                    Photos will appear automatically as they arrive from all sources. Guests joining now will see them in real-time.
                  </p>
                </div>

                <InviteLinkPanel
                  album={createdAlbum}
                  onTokenRegenerated={(updated) => setCreatedAlbum(updated)}
                />

                <div className={styles['nav-bar-end']}>
                  <Button onClick={handleClose}>Done</Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Modal>

      {/* Skip overlays warning dialog */}
      <Modal isOpen={showSkipWarning} onClose={() => setShowSkipWarning(false)} title="Skip Overlays?" size="sm">
        <div className={styles['skip-warning-body']}>
          <div className={styles['skip-warning-inner']}>
            <div className={styles['skip-warning-icon']}>
              <AlertTriangle className={styles['alert-icon-amber']} />
            </div>
            <div>
              <p className={styles['skip-warning-title']}>
                No overlays configured
              </p>
              <p className={styles['skip-warning-text']}>
                Photos arriving without overlays will be added unbranded. You can add overlays later from the album settings.
              </p>
            </div>
          </div>
          <div className={styles['skip-warning-actions']}>
            <Button variant="ghost" onClick={() => setShowSkipWarning(false)}>
              Go Back
            </Button>
            <Button onClick={handleSkipOverlaysConfirmed} loading={albumCreating}>
              Create Without Overlays
            </Button>
          </div>
        </div>
      </Modal>

      {createdAlbum && createdEvent && (
        <A4PosterModal
          isOpen={showPoster}
          onClose={() => setShowPoster(false)}
          albumId={createdAlbum._id}
          albumName={createdAlbum.name}
          eventName={createdEvent.name}
          inviteToken={createdAlbum.inviteToken}
        />
      )}

      <QuotaLimitModal {...quotaModal} onClose={closeQuotaModal} />
    </>
  );
};
